import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSolarPanel,
  faWind,
  faWater,
  faBatteryFull,
  faCog,
  faTools,
  faChartLine,
  faCheckCircle,
  faArrowRight,
  faLeaf,
  faLightbulb,
  faShieldAlt,
  faCheck,
  faAward,
  faHome,
  faIndustry,


} from '@fortawesome/free-solid-svg-icons';

const Services = () => {
  const [activeTab, setActiveTab] = useState('solar');

  const services = {
    solar: {
      title: 'Solar Power Solutions',
      icon: faSolarPanel,
      description: 'Comprehensive solar energy solutions for residential, commercial, and industrial applications.',
      features: [
        'Solar Panel Installation',
        'Grid-Tied Systems',
        'Off-Grid Solutions',
        'Solar Water Heaters',
        'Maintenance & Monitoring',
        'Performance Optimization'
      ],
      benefits: [
        'Reduce electricity bills by up to 90%',
        '25-year warranty on panels',
        'Government incentives available',
        'Low maintenance requirements',
        'Environmentally friendly',
        'Increase property value'
      ],
      href: '/services/solar'
    },
    wind: {
      title: 'Wind Energy Systems',
      icon: faWind,
      description: 'Advanced wind turbine technology for sustainable electricity generation.',
      features: [
        'Onshore Wind Farms',
        'Offshore Installations',
        'Small Wind Turbines',
        'Wind Assessment',
        'Grid Integration',
        'Performance Monitoring'
      ],
      benefits: [
        'High energy output potential',
        '24/7 power generation',
        'Scalable solutions',
        'Long operational life',
        'Minimal land footprint',
        'Predictable energy costs'
      ],
      href: '/services/wind'
    },
    hydro: {
      title: 'Hydroelectric Power',
      icon: faWater,
      description: 'Efficient water-based energy generation systems for sustainable power.',
      features: [
        'Small Hydro Projects',
        'Micro Hydro Systems',
        'Smart Grid Integration for Hydropower',
        'Water Flow Analysis',
        'Environmental Impact Assessment',
        'System Maintenance'
      ],
      benefits: [
        'Consistent power generation',
        'Long-term reliability',
        'Low operating costs',
        'Water resource management',
        'Flood control benefits',
        'Recreation opportunities'
      ],
      href: '/services/hydro'
    },
    storage: {
      title: 'Energy Storage Solutions',
      icon: faBatteryFull,
      description: 'Advanced battery storage systems for energy independence and grid stability.',
      features: [
        'Lithium-Ion Batteries',
        'Flow Battery Systems',
        'Grid-Scale Storage',
        'Residential Storage',
        'Backup Power Systems',
        'Smart Energy Management'
      ],
      benefits: [
        'Energy independence',
        'Peak demand management',
        'Grid stability support',
        'Emergency backup power',
        'Cost optimization',
        'Renewable integration'
      ],
      href: '/services/storage'
    }
  };

  return (
    <div className="services">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary-green to-secondary-blue text-white py-20 lg:py-32 text-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="/images/service hero.png"
            alt="Corenova Services Hero Background"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-green/40 to-secondary-blue/40"></div>
        </div>

        {/* Content */}
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto space-y-6">
            <h1 className="text-4xl md:text-6xl font-bold animate-fade-in text-white drop-shadow-2xl shadow-black">
              Our Renewable Energy Services
            </h1>
            <p className="text-xl md:text-2xl animate-fade-in text-white drop-shadow-xl shadow-black font-semibold" style={{ animationDelay: '0.2s' }}>
              Comprehensive solutions for a sustainable energy future
            </p>
          </div>
        </div>
      </section>

      {/* Service Introduction Section */}
      <section className="section bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <div>
                <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-secondary-blue rounded-2xl flex items-center justify-center mb-6">
                  <FontAwesomeIcon icon={faLeaf} className="text-2xl text-white" />
                </div>

                <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6">
                  Powering a Sustainable Future
                </h2>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 bg-primary-green rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                  </div>
                  <p className="text-gray-700">
                    We're committed to a greener, cleaner future at Corenova Renewable Energy. Our mission is
                    to deliver sustainable solutions that electrify homes, businesses, and communities with clean,
                    reliable energy.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 bg-primary-green rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                  </div>
                  <p className="text-gray-700">
                    Each service we offer is intended to decrease dependency on traditional power sources while
                    leading the way to a more sustainable future.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 bg-primary-green rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                  </div>
                  <p className="text-gray-700">
                    With Corenova Renewable Energy, you get more than a service company—
                    you get a partner committed to guiding you toward renewable energy with confidence.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-green">400+</div>
                  <div className="text-sm text-gray-600">Projects Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary-blue">200+</div>
                  <div className="text-sm text-gray-600">Happy Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-green">1+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
              </div>
            </div>

            {/* Right Column - Features Grid */}
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-primary-green/10 to-primary-green/5 p-6 rounded-2xl border border-primary-green/20">
                  <div className="w-12 h-12 bg-primary-green rounded-xl flex items-center justify-center mb-4">
                    <FontAwesomeIcon icon={faSolarPanel} className="text-xl text-white" />
                  </div>
                  <h3 className="font-semibold text-dark mb-2">Solar Expertise</h3>
                  <p className="text-sm text-gray-600">
                    Professional solar solutions tailored to your specific energy needs and requirements.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-secondary-blue/10 to-secondary-blue/5 p-6 rounded-2xl border border-secondary-blue/20">
                  <div className="w-12 h-12 bg-secondary-blue rounded-xl flex items-center justify-center mb-4">
                    <FontAwesomeIcon icon={faWind} className="text-xl text-white" />
                  </div>
                  <h3 className="font-semibold text-dark mb-2">Wind Integration</h3>
                  <p className="text-sm text-gray-600">
                    Scalable wind energy systems for residential, commercial, and community applications.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-primary-green/10 to-primary-green/5 p-6 rounded-2xl border border-primary-green/20">
                  <div className="w-12 h-12 bg-primary-green rounded-xl flex items-center justify-center mb-4">
                    <FontAwesomeIcon icon={faWater} className="text-xl text-white" />
                  </div>
                  <h3 className="font-semibold text-dark mb-2">Hydro Solutions</h3>
                  <p className="text-sm text-gray-600">
                    Efficient hydroelectric systems with minimal environmental impact and maximum reliability.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-secondary-blue/10 to-secondary-blue/5 p-6 rounded-2xl border border-secondary-blue/20">
                  <div className="w-12 h-12 bg-secondary-blue rounded-xl flex items-center justify-center mb-4">
                    <FontAwesomeIcon icon={faCog} className="text-xl text-white" />
                  </div>
                  <h3 className="font-semibold text-dark mb-2">Maintenance & Support</h3>
                  <p className="text-sm text-gray-600">
                    Comprehensive maintenance services to ensure optimal performance of your renewable systems.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-primary-green rounded-full flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faAward} className="text-2xl text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-dark">Certified Excellence</h4>
                    <p className="text-sm text-gray-600">
                      Fully licensed and certified professionals ensuring quality service
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>


        </div>
      </section>

      {/* Service Tabs */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {Object.keys(services).map((key) => (
                <button
                  key={key}
                  className={`flex flex-col items-center gap-3 p-6 rounded-2xl font-medium transition-all duration-300 ${activeTab === key
                      ? 'bg-gradient-to-r from-primary-green to-secondary-blue text-white shadow-lg'
                      : 'bg-gray-light text-dark hover:bg-primary-green/10'
                    }`}
                  onClick={() => setActiveTab(key)}
                >
                  <FontAwesomeIcon icon={services[key].icon} className="text-2xl" />
                  <span className="text-sm text-center">{services[key].title}</span>
                </button>
              ))}
            </div>

            <div className="bg-gray-light rounded-3xl p-8 lg:p-12">
              <div className="animate-fade-in">
                <div className="flex flex-col lg:flex-row items-start gap-8 mb-12 pb-8 border-b-2 border-gray-200">
                  <div className="w-20 h-20 bg-gradient-to-r from-primary-green to-secondary-blue rounded-2xl flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={services[activeTab].icon} className="text-3xl text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl lg:text-4xl font-bold text-dark mb-4">{services[activeTab].title}</h2>
                    <p className="text-lg text-gray leading-relaxed">{services[activeTab].description}</p>
                    <div className="mt-6">
                      <a href={services[activeTab].href} className="btn bg-primary-green text-white inline-flex items-center gap-2">
                        Learn more <FontAwesomeIcon icon={faArrowRight} />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-12">
                  <div className="space-y-6">
                    <h3 className="text-2xl font-bold text-dark text-center">What We Offer</h3>
                    <div className="space-y-4">
                      {services[activeTab].features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-4 p-4 bg-white rounded-xl hover:bg-primary-green/5 transition-all duration-300">
                          <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green text-xl flex-shrink-0" />
                          <span className="text-dark font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h3 className="text-2xl font-bold text-dark text-center">Key Benefits</h3>
                    <div className="space-y-4">
                      {services[activeTab].benefits.map((benefit, index) => (
                        <div key={index} className="flex items-center gap-4 p-4 bg-white rounded-xl hover:bg-primary-green/5 transition-all duration-300">
                          <FontAwesomeIcon icon={faLeaf} className="text-primary-green text-xl flex-shrink-0" />
                          <span className="text-dark font-medium">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6">
              Our Comprehensive Solar Services
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Full-service solar solutions from consultation to maintenance, using top-tier technology for maximum efficiency and savings.
            </p>
          </div>



          <div className="grid md:grid-cols-3 gap-8">
            {/* Residential Solar */}
            <div className="card group hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-primary-green-light rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faHome} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-dark mb-4">Residential Solar Solutions</h3>
              <p className="text-gray-700 mb-6">
                Our home solar systems are built to last, and endure the test of time, which is why we are locally
                famous for the energy-efficient panels that make home owners money in their pockets.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Energy-efficient panels</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Durable construction</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Maximum savings</span>
                </li>
              </ul>

            </div>

            {/* Commercial Solar */}
            <div className="card group hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-secondary-blue to-secondary-blue-dark rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faIndustry} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-dark mb-4">Commercial Solar Solutions</h3>
              <p className="text-gray-700 mb-6">
                Our commercial solar systems provide businesses with a range of turnkey, low-carbon
                solutions to address rising energy costs, sustainability goals, and the need for resiliency and
                power independence.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Turnkey solutions</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Carbon reduction</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Power independence</span>
                </li>
              </ul>

            </div>

            {/* Solar EPC */}
            <div className="card group hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-secondary-blue rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faCog} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-dark mb-4">Solar Product Supply & EPC</h3>
              <p className="text-gray-700 mb-6">
                We offer high-quality solar products procurement and comprehensive EPC services,
                guaranteeing system design, equipment installation and regular maintenance for the
                most effective renewable energy solution.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Quality equipment</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">End-to-end service</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Maintenance support</span>
                </li>
              </ul>

            </div>
          </div>


        </div>
      </section>

      {/* Additional Services */}
      <section className="section bg-gray-light">
        <div className="container">
          <div className="section-title">
            <h2>Supporting Services</h2>
            <p>Complete solutions to ensure your renewable energy project success</p>
            <div className="mt-4">
              <a href="/services/supporting" className="btn bg-primary-green text-white inline-flex items-center gap-2">
                View Supporting Services <FontAwesomeIcon icon={faArrowRight} />
              </a>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="card group">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-primary-green-light rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faCog} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-dark mb-4">Installation & Setup</h3>
              <p className="text-gray mb-6 leading-relaxed">Professional installation services with certified technicians and quality assurance.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Expert installation team
                </li>
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Quality assurance protocols
                </li>
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Safety compliance
                </li>
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Testing & commissioning
                </li>
              </ul>
            </div>

            <div className="card group">
              <div className="w-16 h-16 bg-gradient-to-r from-secondary-blue to-secondary-blue-dark rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faTools} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-dark mb-4">Maintenance & Repair</h3>
              <p className="text-gray mb-6 leading-relaxed">Regular maintenance services to ensure optimal performance and longevity.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Preventive maintenance
                </li>
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  24/7 emergency support
                </li>
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Performance monitoring
                </li>
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Warranty services
                </li>
              </ul>
            </div>

            <div className="card group">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-secondary-blue rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faChartLine} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-dark mb-4">Consulting & Planning</h3>
              <p className="text-gray mb-6 leading-relaxed">Expert consultation for optimal system design and energy planning.</p>
              {/* <a href="/services/consulting" className="btn bg-primary-green text-white inline-flex items-center gap-2 mb-4">
                Learn more <FontAwesomeIcon icon={faArrowRight} />
              </a> */}
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Energy audits
                </li>
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  System design
                </li>
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Feasibility studies
                </li>
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  ROI analysis
                </li>
              </ul>
            </div>

            <div className="card group">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-primary-green-light rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faShieldAlt} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-dark mb-4">Warranty & Support</h3>
              <p className="text-gray mb-6 leading-relaxed">Comprehensive warranty coverage and ongoing technical support.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Extended warranties
                </li>
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Technical support
                </li>
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Parts replacement
                </li>
                <li className="flex items-center gap-2 text-sm text-dark">
                  <span className="text-primary-green font-bold">✓</span>
                  Performance guarantees
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section bg-white">
        <div className="container">
          <div className="section-title">
            <h2>Our Process</h2>
            <p>How we deliver exceptional renewable energy solutions</p>
            <div className="mt-4">
              <a href="/services/process" className="btn bg-primary-green text-white inline-flex items-center gap-2">
                View Our Process <FontAwesomeIcon icon={faArrowRight} />
              </a>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-8 bg-gray-light rounded-2xl hover:-translate-y-2 transition-all duration-300 hover:shadow-lg group">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-primary-green-light rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white group-hover:scale-110 transition-transform duration-300">
                1
              </div>
              <h4 className="text-xl font-bold text-dark mb-4">Consultation</h4>
              <p className="text-gray leading-relaxed">Initial assessment and energy audit to understand your needs and goals.</p>
            </div>

            <div className="text-center p-8 bg-gray-light rounded-2xl hover:-translate-y-2 transition-all duration-300 hover:shadow-lg group">
              <div className="w-16 h-16 bg-gradient-to-r from-secondary-blue to-secondary-blue-dark rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white group-hover:scale-110 transition-transform duration-300">
                2
              </div>
              <h4 className="text-xl font-bold text-dark mb-4">Design</h4>
              <p className="text-gray leading-relaxed">Custom system design optimized for your location and energy requirements.</p>
            </div>

            <div className="text-center p-8 bg-gray-light rounded-2xl hover:-translate-y-2 transition-all duration-300 hover:shadow-lg group">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-secondary-blue rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white group-hover:scale-110 transition-transform duration-300">
                3
              </div>
              <h4 className="text-xl font-bold text-dark mb-4">Installation</h4>
              <p className="text-gray leading-relaxed">Professional installation by certified technicians with quality assurance.</p>
            </div>

            <div className="text-center p-8 bg-gray-light rounded-2xl hover:-translate-y-2 transition-all duration-300 hover:shadow-lg group">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-primary-green-light rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white group-hover:scale-110 transition-transform duration-300">
                4
              </div>
              <h4 className="text-xl font-bold text-dark mb-4">Support</h4>
              <p className="text-gray leading-relaxed">Ongoing maintenance, monitoring, and support to ensure optimal performance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-gradient-to-br from-primary-green to-secondary-blue text-white py-20">
  <div className="container text-center">
    <div className="max-w-4xl mx-auto space-y-8">
      <h2 className="text-4xl md:text-6xl font-bold leading-tight">
        Ready to Start Your Renewable Energy Journey?
      </h2>
      <p className="text-xl md:text-2xl opacity-90 leading-relaxed">
        Get a free consultation and quote for your renewable energy project
      </p>
      <div className="pt-4">
        <a href 
                  ="/contact" 
                  className="btn bg-white text-primary-green hover:bg-gray-100 px-10 py-4 text-lg font-semibold inline-flex items-center justify-center gap-3"
                >
                  Request Detailed Quote
                  <FontAwesomeIcon icon={faArrowRight} className="text-lg" />
                </a>
      </div>
    </div>
  </div>
</section>
    </div>
  );
};

export default Services; 